import { useState, FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import { Button } from '@/components/ui/button'
import {
  Field, FieldGroup, FieldLabel, FieldSet,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select'
import { extractErr } from '@/components/ui/inline-edit'

type Skill = {
  id: number
  name: string
  category: string
  description: string
  is_system: boolean
}

const CATEGORY_OPTIONS = [
  { value: 'PROGRAMMING', label: 'Programming language' },
  { value: 'FRAMEWORK',   label: 'Framework / library' },
  { value: 'DATABASE',    label: 'Database' },
  { value: 'CLOUD',       label: 'Cloud & Infrastructure' },
  { value: 'TOOL',        label: 'Tool' },
  { value: 'METHODOLOGY', label: 'Methodology' },
  { value: 'INDUSTRY',    label: 'Industry / domain' },
  { value: 'SOFT',        label: 'Soft skill' },
  { value: 'LANGUAGE',    label: 'Spoken language' },
  { value: 'OTHER',       label: 'Other' },
]
const CATEGORY_LABEL: Record<string, string> = Object.fromEntries(
  CATEGORY_OPTIONS.map((o) => [o.value, o.label]),
)

type SkillInput = {
  name: string
  category: string
  description: string
}

const emptySkill: SkillInput = { name: '', category: 'OTHER', description: '' }

export default function ConsultingSkills() {
  const qc = useQueryClient()

  const skillsQ = useQuery({
    queryKey: ['consulting', 'skills'],
    queryFn: async () => {
      const r = await api.get('/consulting/skills/')
      const body = r.data
      if (Array.isArray(body)) return body as Skill[]
      if (body && Array.isArray(body.results)) return body.results as Skill[]
      return [] as Skill[]
    },
  })

  const [editing, setEditing] = useState<Skill | null>(null)
  const [creating, setCreating] = useState(false)

  const createMut = useMutation({
    mutationFn: async (payload: SkillInput) =>
      (await api.post('/consulting/skills/', payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'skills'] })
      setCreating(false)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const updateMut = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: SkillInput }) =>
      (await api.patch(`/consulting/skills/${id}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'skills'] })
      setEditing(null)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/consulting/skills/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consulting', 'skills'] }),
    onError: (e) => toast.error(extractErr(e)),
  })

  const skills = skillsQ.data || []
  const grouped: Record<string, Skill[]> = {}
  skills.forEach((s) => {
    if (!grouped[s.category]) grouped[s.category] = []
    grouped[s.category].push(s)
  })
  const orderedKeys = CATEGORY_OPTIONS.map((o) => o.value).filter((k) => grouped[k])

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs">
            <Link to="/admin/consulting/consultants" className="text-muted-foreground hover:text-foreground hover:underline">← Profiles</Link>
          </div>
          <h2 className="text-2xl font-bold text-foreground mt-1">
            Skill catalog ({skills.length})
          </h2>
          <p className="text-sm text-muted-foreground">
            Controlled vocabulary used in consultant profiles. Seeded entries are read-only.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>+ New skill</Button>
      </div>

      {skillsQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-4">
          {orderedKeys.map((cat) => (
            <div key={cat} className="bg-card rounded-lg shadow overflow-hidden">
              <div className="px-4 py-2 bg-muted/30 border-b text-xs uppercase tracking-wide font-semibold text-muted-foreground">
                {CATEGORY_LABEL[cat]}
              </div>
              <div className="divide-y">
                {grouped[cat].map((s) => (
                  <div key={s.id} className="px-4 py-3 flex items-start justify-between hover:bg-muted/30">
                    <div>
                      <div className="font-medium">
                        {s.name}
                        {s.is_system && (
                          <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">system</span>
                        )}
                      </div>
                      {s.description && <p className="text-sm text-muted-foreground">{s.description}</p>}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <button onClick={() => setEditing(s)} className="text-blue-600 hover:underline text-sm">
                        Edit
                      </button>
                      {!s.is_system && (
                        <button
                          onClick={() => { if (confirm(`Delete ${s.name}?`)) deleteMut.mutate(s.id) }}
                          className="text-red-600 hover:underline text-sm"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <SkillDialog
          initial={editing ? { name: editing.name, category: editing.category, description: editing.description } : emptySkill}
          isEdit={!!editing}
          systemLocked={editing?.is_system || false}
          submitting={createMut.isPending || updateMut.isPending}
          onCancel={() => { setCreating(false); setEditing(null) }}
          onSubmit={(payload) => {
            if (editing) updateMut.mutate({ id: editing.id, payload })
            else createMut.mutate(payload)
          }}
        />
      )}
    </AdminLayout>
  )
}

function SkillDialog({
  initial, isEdit, systemLocked, onSubmit, onCancel, submitting,
}: {
  initial: SkillInput
  isEdit: boolean
  systemLocked: boolean
  onSubmit: (p: SkillInput) => void
  onCancel: () => void
  submitting: boolean
}) {
  const [form, setForm] = useState<SkillInput>(initial)
  const set = <K extends keyof SkillInput>(k: K, v: SkillInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const submit = (e: FormEvent) => {
    e.preventDefault()
    onSubmit(form)
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-card rounded-lg shadow-2xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold mb-3">{isEdit ? 'Edit skill' : 'New skill'}</h3>
        <form onSubmit={submit}>
          <FieldSet>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="skill-name">Name</FieldLabel>
                <Input
                  id="skill-name"
                  required
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  disabled={systemLocked}
                />
                {systemLocked && (
                  <p className="text-xs text-muted-foreground mt-1">System skill — name is read-only.</p>
                )}
              </Field>
              <Field>
                <FieldLabel htmlFor="skill-cat">Category</FieldLabel>
                <Select value={form.category} onValueChange={(v) => set('category', v)}>
                  <SelectTrigger id="skill-cat"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field>
                <FieldLabel htmlFor="skill-desc">Description</FieldLabel>
                <Textarea
                  id="skill-desc"
                  rows={2}
                  value={form.description}
                  onChange={(e) => set('description', e.target.value)}
                />
              </Field>
            </FieldGroup>
          </FieldSet>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
            <Button type="submit" disabled={submitting}>{submitting ? 'Saving…' : 'Save'}</Button>
          </div>
        </form>
      </div>
    </div>
  )
}
